import React, { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LoaderIcon } from "react-hot-toast";
import { getEmployerAdminTransactionInfo } from "../../../actions/employer";
import StatBox from "../../../components/stat-box ";
import dispatch from "../../../middleware";
import { EmployerTransactionInfo } from "../../../models";
import { formatToIndianShortNumber, getErrorMsgFromResponse } from "../../../utils";
import "./dashboardStats.css";

const disbursalTiles = [
  { field: "dailyDisbursalCount", label: "Daily Disbursal Count" },
  { field: "dailyDisbursalSum", label: "Daily Disbursal Sum" },
  { field: "monthlyDisbursalCount", label: "Monthly Disbursal Count" },
  { field: "monthlyDisbursalSum", label: "Monthly Disbursal Sum" },
] as const;

interface MonthOption {
  label: string;
  value: string;
  month: string;
  year: string;
}

interface FilterError {
  message: string;
  isNotFound: boolean;
}

function getLast12Months(): MonthOption[] {
  const options: MonthOption[] = [];
  const currentDate = new Date();

  for (let i = 0; i < 12; i++) {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    const monthName = d.toLocaleString("en-US", { month: "long" });
    const year = d.getFullYear().toString();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    options.push({
      label: `${monthName} ${year}`,
      value: `${year}-${month}`,
      month,
      year,
    });
  }

  return options;
}

interface DashboardStatsTileProps {
  employerTransactionInfo: EmployerTransactionInfo | null;
}

export default function DashboardStatsTile({
  employerTransactionInfo,
}: DashboardStatsTileProps) {
  const storeDispatch = useDispatch();
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorInfo, setErrorInfo] = useState<FilterError | null>(null);
  const [requestedMonthData, setRequestedMonthData] = useState<{
    count: number;
    sum: number;
  } | null>(null);

  // Guard against race conditions on rapid dropdown changes
  const requestIdRef = useRef<number>(0);

  const monthOptions = useMemo(() => getLast12Months(), []);
  const selectedMonthOption = useMemo(
    () => monthOptions.find((opt) => opt.value === selectedMonth),
    [monthOptions, selectedMonth],
  );

  const handleMonthChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedMonth(value);
    setErrorInfo(null);

    if (!value) {
      setRequestedMonthData(null);
      return;
    }

    const [year, month] = value.split("-");
    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);

    try {
      const res: any = await dispatch(
        storeDispatch,
        getEmployerAdminTransactionInfo({ month, year }),
      );

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      const count = res?.requestedMonthDisbursalCount;
      const sum = res?.requestedMonthDisbursalSum;

      if (typeof count === "number" && typeof sum === "number") {
        setRequestedMonthData({ count, sum });
        setErrorInfo(null);
      } else {
        setRequestedMonthData(null);
        setErrorInfo({
          message: "Data not available for the selected month.",
          isNotFound: true,
        });
      }
    } catch (error: any) {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      console.error("Error fetching transaction info for selected month:", error);
      setRequestedMonthData(null);

      const errorMsg = getErrorMsgFromResponse(
        error,
        "Something went wrong. Please try again.",
      );
      
      const isNotFound =
        error?.status === 404 ||
        error?.response?.status === 404 ||
        errorMsg.toLowerCase().includes("not found");

      setErrorInfo({
        message: errorMsg,
        isNotFound,
      });
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const formatDisplayNumber = (num: number): string => {
    return formatToIndianShortNumber(num) || (num === 0 ? "0" : "");
  };

  const selectedMonthLabel = selectedMonthOption ? selectedMonthOption.label : "";

  return (
    <div className="row dashboard-stats-split-layout">
      {/* Left side: Current dashboard tiles */}
      <div className="col-12 col-lg-6 pr-lg-4 dashboard-stats-left-section">
        <div
          className="row"
          style={{
            display: "flex",
            rowGap: "16px",
          }}
        >
          <div className="col-6 mb-3 dashboard-stats-card-col">
            <StatBox
              label="Total Disbursed"
              value={
                formatToIndianShortNumber(
                  employerTransactionInfo?.totalDisbursal,
                ) ?? null
              }
            />
          </div>
          <div className="col-6 mb-3 dashboard-stats-card-col">
            <StatBox
              label="Total Pending Deductible"
              value={
                formatToIndianShortNumber(employerTransactionInfo?.totalEmiDue) ??
                null
              }
            />
          </div>
          {employerTransactionInfo?.totalActiveLoans && (
            <div className="col-6 mb-3 dashboard-stats-card-col">
              <StatBox
                label="Total Active Loans"
                value={
                  formatToIndianShortNumber(
                    employerTransactionInfo?.totalActiveLoans,
                  ) ?? null
                }
              />
            </div>
          )}
          {employerTransactionInfo?.totalForeclosureByUserTillDate && (
            <div className="col-6 mb-3 dashboard-stats-card-col">
              <StatBox
                label="User foreclosed loans"
                value={
                  formatToIndianShortNumber(
                    employerTransactionInfo?.totalForeclosureByUserTillDate,
                  ) ?? null
                }
              />
            </div>
          )}

          {disbursalTiles.map(({ field, label }) =>
            employerTransactionInfo?.[field] ? (
              <div className="col-6 mb-3 dashboard-stats-card-col" key={field}>
                <StatBox
                  label={label}
                  value={
                    formatToIndianShortNumber(employerTransactionInfo?.[field]) ??
                    null
                  }
                />
              </div>
            ) : null,
          )}
        </div>
      </div>

      {/* Right side: Partition and Filter + Filtered Month Cards */}
      <div className="col-12 col-lg-6 pl-lg-4 dashboard-stats-right-section">
        <div className="dashboard-stats-filter-card bg-white p-3 mb-3">
          <label
            htmlFor="dashboard-month-filter"
            className="primary-color fw-600 fs-13 mb-2 d-block"
          >
            Filter by Month
          </label>
          <select
            id="dashboard-month-filter"
            className="form-control dashboard-stats-month-select w-100"
            value={selectedMonth}
            onChange={handleMonthChange}
          >
            <option value="">Select Month</option>
            {monthOptions.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {selectedMonth && (
          <div className="row">
            {isLoading ? (
              <div
                className="col-12 d-flex justify-content-center align-items-center py-4 bg-white mb-3"
                style={{ borderRadius: 8 }}
              >
                <LoaderIcon style={{ height: "30px", width: "30px" }} />
              </div>
            ) : errorInfo ? (
              <div className="col-12 mb-3">
                <div
                  className="p-3 d-flex align-items-center justify-content-center bg-white"
                  style={{
                    borderRadius: 8,
                    border: errorInfo.isNotFound
                      ? "1px solid #E2E8F0"
                      : "1px solid #FEB2B2",
                    backgroundColor: errorInfo.isNotFound
                      ? "#FFFFFF"
                      : "#FFF5F5",
                  }}
                >
                  <span
                    style={{
                      color: errorInfo.isNotFound ? "#718096" : "#E53E3E",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {errorInfo.message}
                  </span>
                </div>
              </div>
            ) : requestedMonthData ? (
              <>
                <div className="col-6 mb-3 dashboard-stats-card-col">
                  <StatBox
                    label={`${selectedMonthLabel} Disbursal Count`}
                    value={formatDisplayNumber(requestedMonthData.count)}
                  />
                </div>
                <div className="col-6 mb-3 dashboard-stats-card-col">
                  <StatBox
                    label={`${selectedMonthLabel} Disbursal Sum`}
                    value={formatDisplayNumber(requestedMonthData.sum)}
                  />
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}